import { participantPlan } from './components/Participants/ViewListParticipants/participantPlan';
const useCustomDates = (t) => {
    const getLabel = (key) => t(`common.${key}`);

    return {
        lastDay: {
            label: getLabel('dates.lastDay'),
            unit: 'days',
            value: 1,
        },
        lastWeek: {
            label: getLabel('dates.lastWeek'),
            unit: 'days',
            value: 7,
        },
        lastMonth: {
            label: getLabel('dates.lastMonth'),
            unit: 'month',
            value: 1,
        },
        lastSemester: {
            label: getLabel('dates.lastSemester'),
            unit: 'months',
            value: 6,
        },
        lastYear: {
            label: getLabel('dates.lastYear'),
            unit: 'months',
            value: 12,
        },
    };
};

const useDefaultPeriod = (t) => {
    const customDates = useCustomDates(t);
    return customDates.lastMonth;
};

const currVersion = 'v4.5.11';

const useConsumersTableColumns = (t, dso) => {
    const getLabel = (key) => t(`common.table.legend.${key}`);

    return [
        { id: 'name', label: getLabel('name'), width: '35%' },
        {
            id: 'dsoId',
            label: getLabel(dso === 'SIBELGA' ? `dsoIdBelgium` : 'dsoIdFrance'),
            width: '25%',
        },
        { id: 'globalRate', label: getLabel('consumption'), width: '15%' },
        { id: 'localRate', label: getLabel('selfProduction'), width: '15%' },
        { id: 'details', label: '', width: '10%' },
    ];
};

const useProducersTableColumns = (t, dso) => {
    const getLabel = (key) => t(`common.table.legend.${key}`);

    return [
        { id: 'name', label: getLabel('name'), width: '35%' },
        {
            id: 'dsoId',
            label: getLabel(dso === 'SIBELGA' ? `dsoIdBelgium` : 'dsoIdFrance'),
            width: '25%',
        },
        { id: 'globalRate', label: getLabel('production'), width: '15%' },
        { id: 'localRate', label: getLabel('selfConsumption'), width: '15%' },
        { id: 'details', label: '', width: '10%' },
    ];
};

const columnTypes = {
    BILLING: 'billingDetail',
    ENEDIS: 'enedisDetail',
    DSO: 'dsoDetails',
};

const getParticipantsTableColumns = (
    t,
    selectedOperation,
    billingSubscriptions = [],
    billingPlans = []
) => {
    const getLabel = (key) => t(`common.${key}`);
    switch (selectedOperation?.dso) {
        case 'ENEDIS':
        case 'SER':
        case 'SRD':
        case 'GREENALP':
            return {
                [columnTypes.ENEDIS]: [
                    {
                        id: 'enedis_entityType',
                        name: 'entityType',
                        label: getLabel('table.legend.entityType'),
                        minWidth: 150,
                    },
                    {
                        id: 'enedis_name',
                        name: 'name',
                        label: getLabel('table.legend.identity'),
                        minWidth: 150,
                    },
                    {
                        id: 'enedis_prm',
                        name: 'prm',
                        label: getLabel('table.legend.prmNumber'),
                        minWidth: 120,
                    },
                    {
                        id: 'enedis_padt',
                        name: 'padt',
                        label: getLabel('table.legend.padtNumber'),
                        minWidth: 120,
                    },
                    {
                        id: 'enedis_activation_range_start',
                        name: 'activationRangeStart',
                        label: getLabel('table.legend.entryDate'),
                        minWidth: 120,
                    },
                    {
                        id: 'enedis_activation_range_end',
                        name: 'activationRangeEnd',
                        label: getLabel('table.legend.exitDate'),
                        minWidth: 120,
                    },
                    {
                        id: 'enedis_address',
                        name: 'address',
                        label: getLabel('table.legend.address'),
                        minWidth: 150,
                    },
                    {
                        id: 'enedis_city',
                        name: ['city', 'postCode'],
                        label: getLabel('table.legend.city'),
                        labels: [
                            getLabel('table.legend.city'),
                            getLabel('table.legend.postCode'),
                        ],
                        minWidth: 150,
                    },
                    {
                        id: 'dossier_status',
                        name: 'folder_status',
                        label: getLabel('table.legend.status'),
                        minWidth: 100,
                    },
                    {
                        id: 'account_status',
                        name: 'account_status',
                        label: getLabel('table.legend.account'),
                        minWidth: 100,
                    },
                    {
                        id: 'account_email',
                        name: 'account_email',
                        label: getLabel('table.legend.email'),
                        getValue: (participant) =>
                            participant?.user?.email || '',
                        minWidth: 100,
                    },
                ],
                [columnTypes.BILLING]: [
                    {
                        id: 'billing_name',
                        name: 'billing_name',
                        label: getLabel('table.legend.billingIdentity'),
                        minWidth: 150,
                    },
                    {
                        id: 'billing_address',
                        name: 'address',
                        label: getLabel('table.legend.address'),
                        minWidth: 150,
                    },
                    {
                        id: 'billing_postCode',
                        name: ['city', 'postCode'],
                        label: getLabel('table.legend.city'),
                        minWidth: 150,
                    },
                    {
                        id: 'billing_phoneNumber',
                        name: ['email', 'phoneNumber'],
                        label: getLabel('table.legend.contact'),
                        minWidth: 100,
                    },
                ],
                [columnTypes.DSO]: [],
            };
        case 'SIBELGA':
            const columns = {
                [columnTypes.ENEDIS]: [],
                [columnTypes.DSO]: [
                    {
                        id: 'dso_name',
                        name: 'name',
                        label: getLabel('table.legend.identity'),
                        getValue: (participant) => participant?.name,
                        minWidth: 150,
                    },
                    {
                        id: 'dso_identificer',
                        name: 'dsoId',
                        label: getLabel('table.legend.sibelga.ean'),
                        getValue: (participant) => participant?.dsoId,
                        minWidth: 120,
                    },
                    {
                        id: 'dso_activation_range_start',
                        name: 'activationRangeStart',
                        label: getLabel('table.legend.entryDate'),
                        getValue: (participant) =>
                            participant?.activationRange.length > 0
                                ? participant?.activationRange[0].value
                                : null,
                        minWidth: 120,
                    },
                    {
                        id: 'dso_activation_range_end',
                        name: 'activationRangeEnd',
                        label: getLabel('table.legend.exitDate'),
                        getValue: (participant) =>
                            participant?.activationRange.length > 1
                                ? participant?.activationRange[1].value
                                : null,
                        minWidth: 120,
                    },
                    {
                        id: 'dso_address',
                        name: 'address',
                        label: getLabel('table.legend.address'),
                        getValue: (participant) =>
                            participant?.dsoDetails?.address,
                        minWidth: 150,
                    },
                    {
                        id: 'dso_city',
                        name: ['city', 'postCode'],
                        label: getLabel('table.legend.city'),
                        labels: [
                            getLabel('table.legend.city'),
                            getLabel('table.legend.postCode'),
                        ],
                        getValue: (participant) => [
                            participant?.dsoDetails?.city,
                            participant?.dsoDetails?.postCode,
                        ],
                        minWidth: 150,
                    },
                    {
                        id: 'dossier_status',
                        name: 'folder_status',
                        label: getLabel('table.legend.status'),
                        getValue: (participant) =>
                            participant?.dsoDetails?.folder_status,
                        minWidth: 100,
                    },
                    {
                        id: 'account_status',
                        name: 'account_status',
                        label: getLabel('table.legend.account'),
                        getValue: (participant) =>
                            participant?.dsoDetails?.account_status,
                        minWidth: 100,
                    },
                    {
                        id: 'account_email',
                        name: 'account_email',
                        label: getLabel('table.legend.email'),
                        getValue: (participant) =>
                            participant?.user?.email || '',
                        minWidth: 100,
                    },
                ],
                [columnTypes.BILLING]: [],
            };

            columns[columnTypes.DSO].push({
                id: 'dso_plan',
                name: 'plan',
                label: getLabel('table.legend.plan'),
                getValue: (participant) => {
                    const plan = participantPlan(
                        participant,
                        billingSubscriptions,
                        billingPlans
                    );
                    return plan?.name;
                },
                minWidth: 150,
            });
            return columns;
        case 'FAKE':
        default:
            return {
                [columnTypes.ENEDIS]: [],
                [columnTypes.DSO]: [
                    {
                        id: 'dso_name',
                        name: 'name',
                        label: getLabel('table.legend.identity'),
                        getValue: (participant) => participant?.name,
                        minWidth: 150,
                    },
                    {
                        id: 'dso_identificer',
                        name: 'dsoId',
                        label: getLabel('table.legend.dsoIdentifier'),
                        getValue: (participant) => participant?.dsoId,
                        minWidth: 120,
                    },
                    {
                        id: 'dso_activation_range_start',
                        name: 'activationRangeStart',
                        label: getLabel('table.legend.entryDate'),
                        getValue: (participant) =>
                            participant?.activationRange.length > 0
                                ? participant?.activationRange[0].value
                                : null,
                        minWidth: 120,
                    },
                    {
                        id: 'dso_activation_range_end',
                        name: 'activationRangeEnd',
                        label: getLabel('table.legend.exitDate'),
                        getValue: (participant) =>
                            participant?.activationRange.length > 1
                                ? participant?.activationRange[1].value
                                : null,
                        minWidth: 120,
                    },
                    {
                        id: 'dso_address',
                        name: 'address',
                        label: getLabel('table.legend.address'),
                        getValue: (participant) =>
                            participant?.dsoDetails?.address,
                        minWidth: 150,
                    },
                    {
                        id: 'dso_city',
                        name: ['city', 'postCode'],
                        label: getLabel('table.legend.city'),
                        labels: [
                            getLabel('table.legend.city'),
                            getLabel('table.legend.postCode'),
                        ],
                        getValue: (participant) => [
                            participant?.dsoDetails?.city,
                            participant?.dsoDetails?.postCode,
                        ],
                        minWidth: 150,
                    },
                    {
                        id: 'dossier_status',
                        name: 'folder_status',
                        label: getLabel('table.legend.status'),
                        getValue: (participant) =>
                            participant?.dsoDetails?.folder_status,
                        minWidth: 100,
                    },
                    {
                        id: 'account_status',
                        name: 'account_status',
                        label: getLabel('table.legend.account'),
                        getValue: (participant) =>
                            participant?.dsoDetails?.account_status,
                        minWidth: 100,
                    },
                    {
                        id: 'account_email',
                        name: 'account_email',
                        label: getLabel('table.legend.email'),
                        getValue: (participant) =>
                            participant?.user?.email || '',
                        minWidth: 100,
                    },
                ],
                [columnTypes.BILLING]: [
                    {
                        id: 'billing_name',
                        name: 'name',
                        label: getLabel('table.legend.billingIdentity'),
                        minWidth: 150,
                    },
                    {
                        id: 'billing_address',
                        name: 'address',
                        label: getLabel('table.legend.address'),
                        minWidth: 150,
                    },
                    {
                        id: 'billing_postCode',
                        name: ['city', 'postCode'],
                        label: getLabel('table.legend.city'),
                        minWidth: 150,
                    },
                    {
                        id: 'billing_phoneNumber',
                        name: ['email', 'phoneNumber'],
                        label: getLabel('table.legend.contact'),
                        minWidth: 100,
                    },
                ],
            };
    }
};

const userTypes = {
    CONSUMERS: 'consumers',
    PRODUCERS: 'producers',
};
const useUserTitle = (t) => {
    const getLabel = (key) => t(`common.words.${key}`);
    return {
        consumers: getLabel('consumers'),
        producers: getLabel('producers'),
    };
};

const userUrl = {
    consumers: 'cons',
    producers: 'prod',
};

const useEntityTypes = (t) => {
    return [
        {
            value: 'Professionnel - industriel',
            label: t('common.entityTypes.proIndustrial'),
        },
        {
            value: 'Professionnel - tertiaire public',
            label: t('common.entityTypes.proTertiary'),
        },
        {
            value: 'Professionnel - tertiaire privé',
            label: t('common.entityTypes.proTertiaryPrivate'),
        },
        {
            value: 'Professionnel - artisan',
            label: t('common.entityTypes.proArtisan'),
        },
        {
            value: 'Professionnel - agriculteur',
            label: t('common.entityTypes.proAgricol'),
        },
        {
            value: 'Professionnel - autre',
            label: t('common.entityTypes.proOther'),
        },
        {
            value: 'Particulier - maison individuelle',
            label: t('common.entityTypes.partIndiv'),
        },
        {
            value: 'Particulier - habitat collectif copropriété',
            label: t('common.entityTypes.partCollective'),
        },
        {
            value: 'Particulier - habitat collectif social',
            label: t('common.entityTypes.partCollectiveSocial'),
        },
        {
            value: 'Particulier - autre',
            label: t('common.entityTypes.partOther'),
        },
    ];
};

// segments = [segmentConsumer],or[segmentProducer]
const segments = {
    consumer: [
        { id: 'C5', value: 'C5', label: 'C5 : (BT) <= 36 kVA (Tarif bleu)' },
        { id: 'C4', value: 'C4', label: 'C4 : (BT) > 36 kVA (Tarif jaune)' },
        {
            id: 'C2',
            value: 'C2',
            label: 'C2 : (HTA) Associé à un contrat unique (CU)',
        },
        {
            id: 'C1',
            value: 'C1',
            label: 'C1 : Associé à un contrat CARD-s (Contrat d’Accès au Réseau de Distribution en soutirage)',
        },
    ],
    producer: [
        {
            id: 'P4',
            value: 'P4',
            label: 'P4 : (BT) Petit producteur P <= 36kW',
        },
        {
            id: 'P3',
            value: 'P3',
            label: 'P3 : (BT) Professionnel multi producteur  P <= 36kW',
        },
        { id: 'P2', value: 'P2', label: 'P2 : (BT) 36 kW < P <= 250kW' },
        { id: 'P1', value: 'P1', label: 'P1 : (HTA) P > 250kW' },
    ],
};
const useEnergyTypes = (t) => {
    const getLabel = (key) => t(`common.${key}`);
    return [
        {
            value: 'producer',
            label: getLabel('words.producer'),
        },
        {
            value: 'consumer',
            label: getLabel('words.consumer'),
        },
    ];
};

const useProductionTypes = (t) => {
    const getLabel = (key) => t(`common.${key}`);
    return [
        {
            value: 3,
            label: getLabel('productionTypes.solar'),
        },
        {
            value: 13,
            label: getLabel('productionTypes.hydro'),
        },
        {
            value: 10,
            label: getLabel('productionTypes.wind'),
        },
        {
            value: 11,
            label: getLabel('productionTypes.cogeneration'),
        },
    ];
};

const drawerWidth = 300;
const closeDrawerWidth = 56;

const getSelectedRange = (selectedDates) => {
    const diff = selectedDates.to.diff(selectedDates.from, 'months', true);
    let selectedRange = 1;
    if (diff > 1 && diff <= 3) selectedRange = 3;
    else if (diff > 3 && diff <= 6) selectedRange = 6;
    else if (diff > 6 && diff <= 12) selectedRange = 12;
    return selectedRange;
};

const crumbTypes = {
    LINK: 'link',
    CONST: 'const',
    MENU: 'menu',
};
const operationTypes = {
    ESTATE: 'patrimoniale',
    OPEN: 'ouverte',
    SOCIAL_BLANK_PERIOD: 'sociale',
    SOCIAL_NO_BLANK_PERIOD: 'sociale sans période blanche',
    P2P: 'Pair à pair',
    SAME_BUILDING: "Au sein d'un même bâtiment",
    ENERGY_COMMUNITY: "Communauté d'énergie",
};

const allocationTypes = {
    static: 'Statique',
    by_default: 'Dynamique par défaut',
    by_weight: 'Par part unitaire',
    by_priority: 'Par priorité',
    by_equality: 'Égalitaire',
    by_pool: 'Par groupe',
    by_ungrouped_priority: 'Full dynamique',
    simple_dynamic: 'Dynamique simple',
};

const featureToggles = {};

export {
    segments,
    useCustomDates,
    useDefaultPeriod,
    currVersion,
    useConsumersTableColumns,
    useProducersTableColumns,
    userTypes,
    useUserTitle,
    userUrl,
    useEntityTypes,
    useEnergyTypes,
    useProductionTypes,
    drawerWidth,
    closeDrawerWidth,
    getSelectedRange,
    crumbTypes,
    getParticipantsTableColumns,
    columnTypes,
    featureToggles,
    operationTypes,
    allocationTypes,
};
